import cx from "classnames";
import { readFragment, ResultOf } from "gql.tada";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useLenis } from "lenis/react";
import { usePage } from "@hooks/usePage";
import kebabCase from "lodash/kebabCase";
import {
  SolutionsChapterFragment,
  SolutionsSectionById,
} from "@queries/entries/SolutionsSectionById";
import SolutionsChapter from "./SolutionsChapter";
import SolutionsDesktopMenu from "./SolutionsDesktopMenu";
import SolutionsMobileMenu from "./SolutionsMobileMenu";

export const SolutionsSection = ({
  entry: { solutionsSection: entry },
}: {
  entry: ResultOf<typeof SolutionsSectionById>;
}) => {
  const location = useLocation();

  const currentPage = usePage();
  const pageTitle = entry?.title ?? currentPage?.pageInfo?.title ?? "Solutions";

  const sectionRef = useRef<HTMLDivElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const chapters = (entry?.chaptersCollection?.items ?? [])
    .map((item) => readFragment(SolutionsChapterFragment, item))
    .filter(Boolean);

  const chapterRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [activeChapterIndex, setActiveChapterIndex] = useState(0);
  const chapterAmountsInViewRef = useRef<number[]>([]);

  const lenis = useLenis();
  const hashScrollHandledRef = useRef(false);

  // Scroll to the chapter targeted by the URL hash on initial load. The
  // chapter media renders without reserved space, so the page grows as media
  // above the target loads; keep re-anchoring on document height changes
  // until the user takes over scrolling.
  useEffect(() => {
    if (!lenis || hashScrollHandledRef.current) return;

    const hash = location.hash;
    if (!hash) return;
    const chapterEl = document.getElementById(hash.slice(1));
    if (!chapterEl) return;

    hashScrollHandledRef.current = true;

    const scrollToChapter = () => {
      // Lenis clamps scroll targets to its cached page height, which is
      // stale right after the page content mounts; re-measure first
      lenis.resize();
      lenis.scrollTo(chapterEl);
    };

    // Wait a frame so this runs after useScrollReset's scroll-to-top
    const rafId = requestAnimationFrame(scrollToChapter);

    const resizeObserver = new ResizeObserver(scrollToChapter);
    resizeObserver.observe(document.body);

    const userScrollEvents = ["wheel", "touchstart", "keydown", "pointerdown"];
    const stopReanchoring = () => {
      resizeObserver.disconnect();
      userScrollEvents.forEach((event) =>
        window.removeEventListener(event, stopReanchoring)
      );
    };
    userScrollEvents.forEach((event) =>
      window.addEventListener(event, stopReanchoring, { passive: true })
    );

    return () => {
      cancelAnimationFrame(rafId);
      stopReanchoring();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lenis]);

  useEffect(() => {
    const updateActiveChapter = () => {
      let mostVisibleIndex = 0;
      let maxVisibility = 0;

      chapterAmountsInViewRef.current.forEach((visibility, index) => {
        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisibleIndex = index;
        }
      });

      setActiveChapterIndex((currentIndex) => {
        if (mostVisibleIndex !== currentIndex && maxVisibility > 0) {
          return mostVisibleIndex;
        }
        return currentIndex;
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const visibleArea = entry.intersectionRatio;
          const index = chapterRefs.current.findIndex(
            (el) => el === entry.target
          );

          if (index !== -1) {
            chapterAmountsInViewRef.current[index] = visibleArea;
          }
        });

        updateActiveChapter();
      },
      { root: null, threshold: Array.from({ length: 11 }, (_, i) => i * 0.1) }
    );

    chapterRefs.current.forEach((ref) => ref && observer.observe(ref));

    // Handle window resize to recalculate active chapter
    const handleResize = () => {
      // Force the observer to re-check all elements by disconnecting and reconnecting
      observer.disconnect();
      chapterRefs.current.forEach((ref) => ref && observer.observe(ref));
    };

    let resizeTimeout: ReturnType<typeof setTimeout>;
    const debouncedResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 100);
    };

    window.addEventListener("resize", debouncedResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", debouncedResize);
      observer.disconnect();
    };
  }, [chapters]);

  return (
    <section
      ref={sectionRef}
      className={cx("container-base grid-base py-[120px] transition-colors", {
        "bg-palette-current-deep/20": activeChapterIndex % 2 === 1,
        "bg-palette-current-dark": activeChapterIndex % 2 === 0,
      })}
    >
      <SolutionsDesktopMenu
        chapters={chapters}
        activeChapterIndex={activeChapterIndex}
        setIsExpanded={setIsExpanded}
        pageTitle={pageTitle}
      />
      <div className="dsk:hidden">
        <SolutionsMobileMenu
          chapters={chapters}
          activeChapterIndex={activeChapterIndex}
          setIsExpanded={setIsExpanded}
          isExpanded={isExpanded}
          pageTitle={pageTitle}
          ref={sectionRef}
        />
      </div>
      {chapters.map((chapter, index) => {
        const slug = kebabCase(chapter.menuLabel ?? chapter.eyebrow ?? "");
        return (
          <div
            key={chapter._id}
            id={slug}
            ref={(el) => (chapterRefs.current[index] = el)}
            className="col-span-12 dsk:col-span-17 dsk:col-start-7"
          >
            <SolutionsChapter chapter={chapter} index={index} />
          </div>
        );
      })}
    </section>
  );
};
